import { countedKey } from "@/lib/i18n/plural";
import { useMemo, useState, type ReactNode } from "react";
import { useLocalName } from "@/shared/localName";
import { TextInput } from "@a2z/ui";
import { imageUrl } from "@a2z/ui/media/image-url";
import { useI18n, useTranslate } from "@/lib/i18n";
import { useMoney } from "@/shared/ui";
import { resolveUnitChargedPrice } from "@/modules/pos/lib/pricing";
import type { Category, MenuItem } from "@/lib/api/types";
import { usePaperTill } from "@/shared/posTheme";
import { PaperRegisterBoard } from "./PaperRegister";

/**
 * The menu side of the order view (`Trioty POS.dc.html`, order artboard).
 *
 * The mock's measurements: a 38px search field capped at 340px, a segmented
 * category track, and an auto-fill grid of 160px-minimum tiles at a 10px
 * gutter — 13px radius, a 64px thumbnail, a 12.5px/700 name and a 12px/800
 * price, with an accent count badge on anything already in the order.
 *
 * Search and category are LOCAL state on purpose. They are a lens over a list
 * the till already holds, so putting them in the URL would make the back button
 * undo a filter mid-service rather than leave the screen.
 */
export function MenuBoard({
  menu,
  categories,
  countsByProductId,
  disabled,
  onAdd,
  onOverride,
  onSubstitute,
  onToggleAvailability,
  togglingProductId,
  notice,
  narrow,
  discountPercent = 0,
  fromPrice,
  paperHead,
  phoneLead,
  tablesHref,
  backLabel,
  paperCrumbs,
}: {
  menu: MenuItem[];
  categories: Category[];
  countsByProductId: Map<string, number>;
  disabled: boolean;
  onAdd: (item: MenuItem) => void;
  /**
   * "سعر مختلف" (D46, S9) — opens the amount dialog instead of adding at the
   * catalogue price. `undefined` for everyone who does not hold
   * `RESTAURANT_INVOICE_PRICE_OVERRIDE`, and the control is then not rendered
   * at all: a waiter must never even SEE a control that types an arbitrary
   * price onto a bill.
   */
  onOverride?: (item: MenuItem) => void;
  /**
   * "شيل" (D61, D63) — opens the ingredient-swap dialog for this dish instead
   * of adding it as the menu describes it.
   *
   * `undefined` for everyone without `RESTAURANT_INVOICE_SUBSTITUTE`, and the
   * control is then not rendered at all. Hidden rather than disabled, for the
   * same reason as `onOverride` above: the rung is in no preset below OWNER, so
   * on almost every till a disabled chip would be permanent furniture
   * advertising an authority nobody there can be granted from this screen.
   */
  onSubstitute?: (item: MenuItem) => void;
  /**
   * The tier this order is being taken on (0–100), so the grid quotes the price
   * this table will actually be charged.
   *
   * `MenuItem.price` is the CATALOGUE price, and on a tiered bill it is not the
   * number the customer pays. The picker and the bill were both corrected to
   * price through the tier; the grid was not, so a waiter on a −20% table read
   * `140` off the tile, `112` in the picker a tap later, and `112` again on the
   * bill. The tile is the number said out loud first, so it was the one most
   * likely to be quoted to the customer.
   *
   * Zero — the default, and every untiered order — leaves the catalogue price
   * exactly as it was.
   */
  discountPercent?: number;
  /**
   * 86 / un-86 this item, for a session holding `RESTAURANT_MENU_AVAILABILITY`
   * (D32, §5.8). `undefined` for everyone else, and the control is then not
   * rendered at all.
   *
   * §5.8 says running out is "a service fact settled in one tap at the till",
   * and until now the till was the one surface that could only READ the flag:
   * the cashier who hears "the fish is gone" had to leave the floor, open the
   * back office, find the item and flip a switch inside its details form.
   */
  onToggleAvailability?: (item: MenuItem) => void;
  /** The item whose availability write is in flight, if any. */
  togglingProductId?: string | null;
  /**
   * Degraded-read strips, above everything else in this column.
   *
   * A till whose menu, sections or addon groups failed to load renders an
   * empty list that is indistinguishable from a menu with nothing on it — so
   * the failure is said out loud HERE, beside the list it emptied, rather than
   * in a toast that has already dismissed itself by the time the waiter looks
   * up. Inline is right for the same reason the stale-close recovery is
   * inline: it persists and it carries its own retry.
   */
  notice?: ReactNode;
  /**
   * Whether this board is one half of a SWITCHED pair at narrow widths.
   *
   * `undefined` keeps the legacy stacked behaviour (the pre-order table view,
   * which has no bill to switch to): the board scrolls inside whatever height
   * the order panel leaves it. The invoice screen passes `"show"` or
   * `"hide"`: below `lg` exactly one of the two panes is displayed and the
   * summary bar switches between them, so the hidden one is `display:none`
   * rather than unmounted — the search text and the scroll position a waiter
   * was mid-way through must survive a look at the bill and back.
   *
   * The three values produce three MUTUALLY EXCLUSIVE display utilities, never
   * `flex` and `hidden` on the same element: two utilities setting the same
   * property resolve by stylesheet order, which is not something a component
   * may rely on.
   */
  narrow?: "show" | "hide";
  /**
   * PAPER: the lowest price a dish that asks for a size or extras can be added
   * at — its tile then reads "from" it. `null` for a one-tap dish.
   */
  fromPrice?: (item: MenuItem) => number | null;
  /** PAPER: the Tables / Kitchen tabs over the register. */
  paperHead?: ReactNode;
  /** PAPER, below `lg`: the head line's table and bill (`PaperRegisterBoard`). */
  phoneLead?: ReactNode;
  /** PAPER: where "Tables" and "Back · to Tables" go (the floor by default). */
  tablesHref?: string;
  /** PAPER (B6 · P24b): the Back tile's "to …" line when it is not the tables. */
  backLabel?: string;
  /** PAPER (B6 · P24b): the crumbs on a bill at no table. */
  paperCrumbs?: { root: string; place: string };
}) {
  const t = useTranslate();
  const { locale } = useI18n();
  const localName = useLocalName();
  const money = useMoney();
  const [query, setQuery] = useState("");
  const [categoryId, setCategoryId] = useState<string | null>(null);
  // Same lazy read as the manage screens: a missing VITE_MEDIA_BASE_URL falls
  // back to the placeholder glyph rather than emitting a broken <img>.
  const mediaBase = import.meta.env.VITE_MEDIA_BASE_URL;
  // The PAPER register (2026-10 redesign) on a light till — see below.
  const paper = usePaperTill();

  const visible = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return menu.filter((item) => {
      if (!item.isActive) return false;
      if (categoryId && !item.categoryIds.includes(categoryId)) return false;
      if (!needle) return true;
      return (
        /* BOTH names, whatever the screen's language: a cashier typing
           "fries" must find it on an Arabic till, and someone typing "بطاطس"
           on an English one. Display picks a language; search must not. */
        item.nameAr.toLowerCase().includes(needle) ||
        (item.nameEn ?? "").toLowerCase().includes(needle)
      );
    });
  }, [menu, query, categoryId]);

  // Only categories that actually hold a menu item — an empty tab on a till is
  // a dead tap during service.
  const usableCategories = useMemo(
    () =>
      categories.filter((category) =>
        menu.some(
          (item) => item.isActive && item.categoryIds.includes(category.id),
        ),
      ),
    [categories, menu],
  );

  if (paper) {
    // PAPER (founder, 2026-10-03): the register in two steps — sections, then
    // a section's dishes — with the Back tiles and the crumbs. The tile carries
    // no visible controls: a tap adds, and its price override, swap and 86 are
    // on its press-and-hold sheet (and in the add-on picker).
    return (
      <PaperRegisterBoard
        onToggleAvailability={onToggleAvailability}
        togglingProductId={togglingProductId}
        onOverride={onOverride}
        onSubstitute={onSubstitute}
        menu={menu}
        categories={categories}
        countsByProductId={countsByProductId}
        disabled={disabled}
        onAdd={onAdd}
        discountPercent={discountPercent}
        fromPrice={fromPrice}
        notice={notice}
        narrow={narrow}
        head={paperHead}
        phoneLead={phoneLead}
        tablesHref={tablesHref ?? `/${locale}/pos`}
        backLabel={backLabel}
        crumbs={paperCrumbs}
      />
    );
  }

  return (
    <div
      className={[
        narrow === "hide"
          ? "hidden lg:flex"
          : narrow === "show"
            ? "flex min-h-0"
            : // The stacked table view needs `min-h-0` too. Without it the
              // board refuses to shrink below its content, and inside
              // TablePage's `flex-col-reverse` the overflow goes UPWARD — off
              // the top of the screen, where nothing scrolls — taking the
              // order panel (price tier, guest chips) with it on a phone.
              "flex min-h-0",
        "min-w-0 flex-1 flex-col px-[18px] pt-4",
      ].join(" ")}
    >
      {notice ? <div className="mb-3 shrink-0">{notice}</div> : null}
      <div className="mb-3 flex shrink-0 items-center gap-2.5">
        <div className="w-full max-w-[340px]">
          <TextInput
            name="pos-menu-search"
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            placeholder={t((m) => m.pos.menuSearch)}
            aria-label={t((m) => m.pos.menuSearch)}
            data-testid="menu-search"
          />
        </div>
        <div className="flex-1" />
        <div className="text-[length:calc(var(--font-size-2xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
          {t(
  /* B117/A2 — the DUAL. `plural.ts` holds the rule.
     
     D45b · ruling A1 — and the family is the MENU's, not the bill's. This
     count is "dishes the menu is showing"; `TillSummaryBar` 600px below it
     counts lines on the bill, and until this change both read the same four
     keys, so «٩ أصناف» and «صنفين» on one screen named two different subjects
     with one vocabulary. `lineCount*` stays reached from `OrderPanel.tsx` and
     `TillSummaryBar.tsx`, which is where the bill's count belongs. */
  countedKey(visible.length, {
    one: (m) => m.pos.menuResultCountOne,
    two: (m) => m.pos.menuResultCountTwo,
    few: (m) => m.pos.menuResultCountFew,
    many: (m) => m.pos.menuResultCount,
  }),
  { count: visible.length },
)}
        </div>
      </div>

      {usableCategories.length > 0 ? (
        <div
          role="group"
          data-ui="segmented"
          aria-label={t((m) => m.pos.menuAllCategories)}
          className="mb-3.5 inline-flex max-w-full shrink-0 self-start gap-0.5 overflow-x-auto rounded-[var(--radius-tile)] border border-[color:var(--border-strong)] bg-[color:var(--pos-track)] p-[3px]"
        >
          {[null, ...usableCategories.map((category) => category.id)].map(
            (id) => {
              const isActive = categoryId === id;
              const label =
                id === null
                  ? t((m) => m.pos.menuAllCategories)
                  : (usableCategories.find((category) => category.id === id)
                      ?.nameAr ?? "");
              return (
                <button
                  key={id ?? "all"}
                  type="button"
                  aria-pressed={isActive}
                  data-ui="segmented-tab"
                  data-active={isActive ? "" : undefined}
                  onClick={() => setCategoryId(id)}
                  className={[
                    "shrink-0 whitespace-nowrap rounded-[var(--radius-control)] px-3.5 py-[7px] text-xs font-bold transition-colors",
                    isActive
                      ? "bg-[color:var(--surface)] text-[color:var(--text)] shadow-[var(--shadow-sm)]"
                      : "text-[color:var(--text-muted)] hover:text-[color:var(--text)]",
                  ].join(" ")}
                >
                  {label}
                </button>
              );
            },
          )}
        </div>
      ) : null}

      <div data-ui="board" className="min-h-0 flex-1 overflow-y-auto pb-[18px] pt-0.5">
        {/* The artboard's tile is a 200px-minimum track at a 12px gutter, and
            it is kept from `sm` up. Below that it is arithmetically ONE tile
            per row and the QA saw exactly that: 390px less the board's 18px
            gutters is 354px, and `auto-fill` fits floor((354+12)/212) = 1
            column — a phone menu of full-width rows, each dominated by an
            empty image well.

            A 150px minimum at a 10px gutter fits 2 columns from a 346px
            viewport up, so the 390px design width gets two 172px tiles — a
            154px inner width, where the tile's content (80px image well, a
            truncating name, `320 جنيه` beside the "+ إضافات" hint) still
            reads. Two is the honest ceiling: a third column at 390px would
            leave ~108px per tile, at which the price and the addon hint stop
            sharing a line. The artboard's own numbers stay from `sm` up. */}
        <div className="grid grid-cols-[repeat(auto-fill,minmax(min(150px,100%),1fr))] gap-2.5 sm:grid-cols-[repeat(auto-fill,minmax(min(200px,100%),1fr))] sm:gap-3">
          {visible.map((item) => {
            const inOrder = countsByProductId.get(item.id) ?? 0;
            // 86'd tonight (D32). Disabled, NOT removed: a waiter standing in
            // front of the till has to be able to answer "do you have the
            // fish?", and an item that silently vanished from the grid reads as
            // a bug in the till rather than as an empty pan in the kitchen.
            const soldOut = !item.isAvailable;
            /*
             * B18 — an ingredient a PERSON has called finished. A different
             * fact from `soldOut` above and rendered as a different thing,
             * which is the founder's answer made visible: the tile stays
             * ENABLED and the dish sells, because an ingredient 86 warns and
             * never refuses. A shared badge, or a disabled tile, would have
             * made it a second way to 86 a dish.
             */
            const ingredientsOut = item.ingredientsMarkedOut ?? [];
            /*
             * B93 — DOES THIS TILE CARRY THE CONTROL ROW, and therefore have to
             * leave room for it?
             *
             * The row is absolutely positioned (it has to be: a button inside a
             * button is invalid HTML, so it cannot live in the tile's flow),
             * which means it takes NO space and the name/price row underneath
             * runs straight beneath it. Measured at 1440 in both locales: the
             * price "60 EGP" painted under the "Different price" chip.
             *
             * Padding rather than a shorter price row or a smaller chip: the
             * flow content keeps every pixel it had, and the tile simply admits
             * that its last ~28px are spoken for. Only when the row is actually
             * there — a tile with no controls (and every tile on a till whose
             * cashier has neither permission) must not grow a dead band.
             */
            const hasTileControls = (onOverride || onSubstitute) && !soldOut;
            const ingredientNames = ingredientsOut
              .map((row) => row.name)
              .join("، ");
            return (
              // The tile is wrapped rather than made the positioning context
              // itself: the 86 control is a SECOND control on the same tile,
              // and a button inside a button is invalid HTML that browsers
              // resolve by dropping one of them.
              <div key={item.id} className="relative">
                <button
                  type="button"
                  disabled={disabled || soldOut}
                  aria-disabled={soldOut || undefined}
                  title={
                    soldOut
                      ? // A combo is off because something INSIDE it is off
                        // (S16). Saying so is the difference between a waiter
                        // hunting for a switch nobody flipped and one who knows
                        // to ask the kitchen about the fries.
                        item.unavailableBecause === "COMPONENT"
                        ? t((m) => m.pos.comboSoldOutHint)
                        : t((m) => m.pos.menuSoldOutHint)
                      : // B18: names the ingredients, because "the fish is off"
                        // is actionable at a table and "something is off" is
                        // not. The pill below carries the state; this carries
                        // the list, which will not fit on a 172px tile.
                        ingredientsOut.length > 0
                        ? t((m) => m.pos.menuIngredientOutHint, {
                            items: ingredientNames,
                          })
                        : undefined
                  }
                  data-testid={`menu-item-${item.id}`}
                  onClick={() => onAdd(item)}
                  data-ui="card"
                  className={[
                    "pos-tile relative flex h-full w-full flex-col gap-2 rounded-[var(--radius-panel)] border border-[color:var(--pos-tile-br)] bg-[color:var(--pos-tile)] p-[9px] text-start disabled:opacity-50",
                    /*
                     * B93: the control row sits 6px off the bottom, so a tile
                     * carrying one owes its flow content the row's height plus
                     * that. MEASURED rather than guessed, on a real bill.
                     *
                     * It used to be TWO values — `pb-[52px] sm:pb-[34px]` —
                     * because the chips were 23px tall on a wide tile and
                     * wrapped their own labels to 40px on a narrow one, and one
                     * number could not clear both.
                     *
                     * SLICE 8 COLLAPSED THAT BACK TO ONE, and the reason is a
                     * scope fix two files away rather than anything about this
                     * tile. These two chips carried `.pos-line-control`, the
                     * bill row's exemption from the till's 44px touch floor —
                     * one of seventeen controls wearing an exemption whose own
                     * comment said it applied only inside a `.pos-line` row.
                     * The exemption is now bounded to that row, so the chips
                     * take the floor like every other button a waiter hits
                     * mid-service, and the row is 44px tall at EVERY width.
                     *
                     * Re-measured in a browser, `.pos-dark` on a live bill:
                     *
                     *  · 390px — tile 172px, row 163×44, name/price row clears
                     *    it by 2.9–5px under `pb-[52px]`. Kept.
                     *  · 1440px — tile 237px, row 163×44, and `sm:pb-[34px]`
                     *    put the row 13px THROUGH the name/price row. A real
                     *    overlap, on the screen the till spends service on, and
                     *    the exact defect B93 was written to stop.
                     *
                     * 6px of bottom inset + a 44px row = 50px owed; 52px pays
                     * it with 2px to spare at both widths. The breakpoint is
                     * gone because the thing that needed it is gone: nothing
                     * here now varies by width.
                     */
                    hasTileControls ? "pb-[52px]" : "pb-[11px]",
                  ].join(" ")}
                >
                  {soldOut ? (
                    // The badge sits where the in-order count sits, because the
                    // two can never both be true: an 86'd item cannot be added.
                    <span
                      className="absolute right-1.5 top-1.5 z-[2] flex h-[22px] items-center justify-center rounded-full bg-[color:var(--danger)] px-2 text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--accent-contrast)] shadow-[var(--shadow-sm)]"
                      data-testid={`menu-item-sold-out-${item.id}`}
                    >
                      {t((m) => m.pos.menuSoldOut)}
                    </span>
                  ) : null}
                  {inOrder > 0 ? (
                    <span className="absolute right-1.5 top-1.5 z-[2] flex h-[22px] min-w-[22px] items-center justify-center rounded-full bg-[color:var(--accent)] px-1.5 text-[length:var(--font-size-2xs)] font-extrabold tabular-nums text-[color:var(--accent-contrast)] shadow-[var(--shadow-sm)]">
                      {inOrder}
                    </span>
                  ) : null}
                  <span className="relative flex h-20 items-center justify-center overflow-hidden rounded-[var(--radius-tile)] border border-[color:var(--hair)] bg-[color:var(--pos-thumb)]">
                    {/* B18 — inside the image well, at its BOTTOM.
                        Not the top corners, and the browser round is why: the
                        in-order count and the sold-out badge are hard-pinned
                        top-RIGHT, and on the invoice screen a 44px 86-toggle
                        sits at top-LEFT. Put here first, the badge rendered as
                        "مكوّن خ" with that toggle's icon painted over it — a
                        clip the width sweep called clean, because nothing
                        overflowed the viewport. This corner is claimed by
                        nothing on either screen.

                        A warning tone and a different word from "نفد", never
                        the danger red: an 86'd dish cannot be sold and this one
                        can, and two red pills meaning two different things is
                        how a floor learns to read neither. It coexists with the
                        in-order count on purpose — unlike the sold-out badge,
                        this dish IS being sold. */}
                    {!soldOut && ingredientsOut.length > 0 ? (
                      <span
                        className="absolute bottom-1 left-1 z-[2] flex h-[20px] max-w-[calc(100%-8px)] items-center justify-center truncate rounded-full bg-[color:var(--warning)] px-2 text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--accent-contrast)] shadow-[var(--shadow-sm)]"
                        data-testid={`menu-item-ingredient-out-${item.id}`}
                      >
                        {t((m) => m.pos.menuIngredientOut)}
                      </span>
                    ) : null}
                    {item.imageKey && mediaBase ? (
                      <img
                        src={imageUrl(
                          item.imageKey,
                          { width: 160, fit: "cover", format: "auto" },
                          { baseUrl: mediaBase },
                        )}
                        alt=""
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <svg
                        width="20"
                        height="20"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="1.7"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        className="text-[color:var(--text-disabled)]"
                        aria-hidden="true"
                      >
                        <rect x="3" y="3" width="18" height="18" rx="3" />
                        <circle cx="9" cy="9" r="2" />
                        <path d="m21 15-3.5-3.5L6 23" />
                      </svg>
                    )}
                  </span>
                  <span className="flex min-w-0 flex-col gap-0.5">
                    <span className="truncate text-[length:calc(var(--font-size-sm)_+_0.5px)] font-bold text-[color:var(--text)]">
                      {localName(item)}
                    </span>
                    <span className="flex items-center gap-1.5">
                      <span className="text-[length:calc(var(--font-size-md)_+_1px)] font-extrabold tabular-nums text-[color:var(--text)]">
                        {money(resolveUnitChargedPrice(item.price, discountPercent))}
                      </span>
                      {/* A combo tile says so (S16, D52): the price is for the
                          whole plate, and a waiter reading "120" beside a
                          burger's "100" needs to know which is which. Nothing
                          new to ENTER — a combo is added with one tap like any
                          other item. */}
                      {item.isCombo ? (
                        <span
                          className="rounded-full border border-[color:var(--border-light)] px-[6px] py-px text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-muted)]"
                          data-testid={`menu-item-combo-${item.id}`}
                        >
                          {t((m) => m.pos.comboBadge)}
                        </span>
                      ) : null}
                      {item.addonGroupIds.length > 0 ? (
                        // The artboard reads "320 ج.م + إضافات" — the plus is part
                        // of the hint, not decoration: it says the price is a
                        // starting point, which is what a modifier group means for
                        // the number beside it.
                        <span className="text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.05em] text-[color:var(--text-disabled)]">
                          + {t((m) => m.pos.menuHasAddons)}
                        </span>
                      ) : null}
                    </span>
                  </span>
                </button>

                {/* Manual price (D46, S9). A SECOND control on the tile, same
                    reason the 86 toggle is one: a button inside a button is
                    invalid HTML. It sits on the physical right, under the
                    badges — never over the 86 toggle on the left, and never
                    on a sold-out tile, where there is nothing left to price.

                    `end-1.5`, a LOGICAL inset, not `right-1.5`. The tile's name
                    and price are in normal flow and therefore start at the
                    inline START; this row has to sit at the inline END or it
                    lands on top of them. Pinned to the physical right it
                    cleared the price in English and covered it in Arabic — on
                    every swappable tile, which with a recipe present is every
                    tile, on the screen the till spends all of service on. */}
                {hasTileControls ? (
                  <div className="absolute bottom-1.5 end-1.5 z-[2] flex items-center gap-1">
                    {onOverride ? (
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onOverride(item)}
                        data-testid={`menu-item-override-${item.id}`}
                        data-ui="menu-tile-action"
                        className="rounded-full border border-[color:var(--border-strong)] bg-[color:var(--pos-tile)] px-2 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold uppercase tracking-[0.04em] text-[color:var(--text-muted)] shadow-[var(--shadow-sm)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.priceOverrideAction)}
                      </button>
                    ) : null}
                    {/* Open ingredient swap (D61/D63). A third control on the
                        tile, on the same reasoning as the two beside it: a
                        button inside a button is invalid HTML.

                        No `uppercase` and no `tracking-*` here, unlike the
                        override chip next to it: this label is Arabic, and
                        letter-spacing prises the joined script apart. The
                        neighbour predates that rule and is left alone rather
                        than widened into this change. */}
                    {/* Never on a COMBO. Its food lives on its components, so
                        the server refuses a swap there outright
                        (NOT_SUPPORTED_ON_COMBO, §9/B10) — and its own recipe is
                        usually empty, so the dialog would have opened on an
                        empty list telling the waiter to "ask the office to
                        record the recipe" for a meal deal that has one. */}
                    {onSubstitute && !item.isCombo ? (
                      <button
                        type="button"
                        disabled={disabled}
                        onClick={() => onSubstitute(item)}
                        data-testid={`menu-item-substitute-${item.id}`}
                        data-ui="menu-tile-action"
                        className="rounded-full border border-[color:var(--border-strong)] bg-[color:var(--pos-tile)] px-2 py-[3px] text-[length:var(--font-size-2xs)] font-extrabold text-[color:var(--text-muted)] shadow-[var(--shadow-sm)] transition-colors enabled:hover:text-[color:var(--text)] disabled:opacity-40"
                      >
                        {t((m) => m.pos.swapRemoveLabel)}
                      </button>
                    ) : null}
                  </div>
                ) : null}

                {/* 86 / un-86, at the till (§5.8, D32).

                  A corner icon-button, NOT a long-press and not a swipe: an
                  invented gesture on a shared tablet is a control nobody
                  discovers and everybody triggers by accident. It sits on the
                  physical LEFT because both badges sit on the physical right,
                  and it stays reachable on an 86'd tile — whose own button is
                  disabled — because un-86'ing is the more urgent of the two
                  acts: the pan is refilled and the item has to come back.

                  The 44px box is the POS tap-target floor (`styles.css`);
                  the visible chip inside it is 26px, so the control reads as
                  small and still takes a thumb. */}
                {onToggleAvailability ? (
                  <button
                    type="button"
                    disabled={togglingProductId === item.id}
                    onClick={() => onToggleAvailability(item)}
                    aria-label={
                      soldOut
                        ? t((m) => m.pos.menuUnmarkSoldOut, {
                            name: localName(item),
                          })
                        : t((m) => m.pos.menuMarkSoldOut, { name: localName(item) })
                    }
                    title={
                      soldOut
                        ? t((m) => m.pos.menuUnmarkSoldOutShort)
                        : t((m) => m.pos.menuMarkSoldOutShort)
                    }
                    data-testid={`menu-item-availability-${item.id}`}
                    className="absolute left-0 top-0 z-[3] flex h-11 w-11 items-center justify-center disabled:opacity-40"
                  >
                    {/* The ring is the whole affordance here — this is the one
                        control on the tile with no label — and it was drawn in
                        `--pos-tile-br` on `--pos-tile`, which I measured at
                        1.27:1 in the dark till and 1.25:1 on light. Half the
                        3:1 that 1.4.11 asks of a control's boundary, and the
                        measurement behind the cashier's "the ghost buttons
                        don't read as tappable": on `ar-mobile/pos-table.png`
                        the ⊘ reads as a glyph floating on the photo rather
                        than as something to press.

                        `--text-muted` measures 4.95:1 on the same tile, and it
                        is already the colour of the glyph inside the ring — so
                        the control now draws in ONE ink instead of a legible
                        mark inside an illegible boundary. The sold-out state
                        keeps its filled `--success` ring, which never had the
                        problem. Nothing moves; this is colour only. */}
                    <span
                      className={[
                        "flex h-[26px] w-[26px] items-center justify-center rounded-full border",
                        "shadow-[var(--shadow-sm)] transition-colors",
                        soldOut
                          ? "border-[color:var(--success)] bg-[color:var(--success)] text-[color:var(--accent-contrast)]"
                          : "border-[color:var(--text-muted)] bg-[color:var(--pos-tile)] text-[color:var(--text-muted)]",
                      ].join(" ")}
                    >
                      {soldOut ? (
                        // Coming back: a tick.
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          aria-hidden="true"
                        >
                          <path d="M20 6 9 17l-5-5" />
                        </svg>
                      ) : (
                        // Going off: a struck-through circle, the same glyph the
                        // sold-out state means.
                        <svg
                          width="13"
                          height="13"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.4"
                          strokeLinecap="round"
                          aria-hidden="true"
                        >
                          <circle cx="12" cy="12" r="9" />
                          <path d="m5.6 5.6 12.8 12.8" />
                        </svg>
                      )}
                    </span>
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
        {visible.length === 0 ? (
          <div className="py-12 text-center text-[length:calc(var(--font-size-xs)_+_0.5px)] font-semibold text-[color:var(--text-muted)]">
            {t((m) => m.pos.menuNoResults)}
          </div>
        ) : null}
      </div>
    </div>
  );
}
